import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { Plus, Search, FileText, ChevronDown, ChevronUp } from 'lucide-react';

const PAGE_SIZE = 50;

const PurchaseOrders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [sortField, setSortField] = useState('order_date');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();

  // Sync search to URL
  useEffect(() => {
    if (searchQuery) setSearchParams({ search: searchQuery }, { replace: true });
    else setSearchParams({}, { replace: true });
  }, [searchQuery]); // eslint-disable-line

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  useEffect(() => {
    const fetchPOs = async () => {
      setLoading(true);
      try {
        const params = { skip: (currentPage - 1) * PAGE_SIZE, limit: PAGE_SIZE };
        if (searchQuery.trim()) params.search = searchQuery.trim();
        const data = await api.getPurchaseOrders(params);
        setPos(data);
      } catch (err) {
        console.error('Failed to load POs', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPOs();
  }, [searchQuery, currentPage]);

  // Total count for pagination
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const params = new URLSearchParams();
        if (searchQuery.trim()) params.append('search', searchQuery.trim());
        const res = await api.request(`/api/purchase-orders/count?${params.toString()}`);
        setTotalCount(res?.total || 0);
      } catch (e) { console.error('Failed to load PO count', e); }
    };
    fetchCount();
  }, [searchQuery]);

  // Server already filters; keep client filter as a no-op safety net for stale data
  const filtered = pos;

  const sorted = [...filtered].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    // Date fields: compare as dates
    if (['order_date', 'created_at', 'updated_at'].includes(sortField)) {
      const aDate = new Date(aVal || 0).getTime();
      const bDate = new Date(bVal || 0).getTime();
      return sortDir === 'asc' ? aDate - bDate : bDate - aDate;
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return sortDir === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5" />;
  };

  const statusBadge = (status) => {
    const colors = {
      received: 'bg-green-100 text-green-700',
      ordered: 'bg-blue-100 text-blue-700',
      partial: 'bg-yellow-100 text-yellow-700',
      draft: 'bg-gray-100 text-gray-600',
      cancelled: 'bg-red-100 text-red-700',
    };
    return <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100'}`}>{status}</span>;
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Purchase Orders</h2>
          <p className="text-muted-foreground">{totalCount.toLocaleString()} orders from vendors{searchQuery && ' (filtered)'}</p>
        </div>
        <Link to="/purchase-orders/new"
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
          data-testid="new-purchase-order-btn">
          <Plus className="h-4 w-4" /> New PO
        </Link>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input type="text" placeholder="Search by PO #, vendor, contact, location..."
          value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-input rounded-md" data-testid="po-search" />
      </div>

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('po_number')}>
                  PO # <SortIcon field="po_number" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('created_at')}>
                  Date <SortIcon field="created_at" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('vendor_name')}>
                  Vendor <SortIcon field="vendor_name" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Location</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Items</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('total')}>
                  Total <SortIcon field="total" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  No purchase orders found
                </td></tr>
              ) : (
                sorted.map((po) => (
                  <tr key={po.id} className="hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/purchase-orders/${po.id}`)} data-testid={`po-row-${po.id}`}>
                    <td className="px-4 py-3 font-medium font-mono text-sm">{po.po_number}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                      {fmtDate(po.order_date || po.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <p className="font-medium">{po.vendor_name || '\u2014'}</p>
                      {po.vendor_email && <p className="text-xs text-muted-foreground">{po.vendor_email}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {po.contact_person || '\u2014'}
                      {po.vendor_phone && <p className="text-xs">{po.vendor_phone}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{po.location || '\u2014'}</td>
                    <td className="px-4 py-3">{statusBadge(po.status)}</td>
                    <td className="px-4 py-3 text-sm text-right">{po.line_items?.length || 0}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium tabular-nums">
                      ${(po.total || 0).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground" data-testid="po-pagination">
          <span>
            Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} orders
          </span>
          <div className="flex gap-1">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-3 py-1.5 border border-input rounded-md disabled:opacity-50 hover:bg-muted">
              Previous
            </button>
            <span className="px-3 py-1.5">Page {currentPage} of {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}</span>
            <button onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalCount / PAGE_SIZE), p + 1))}
              disabled={currentPage >= Math.ceil(totalCount / PAGE_SIZE)}
              className="px-3 py-1.5 border border-input rounded-md disabled:opacity-50 hover:bg-muted">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrders;
